import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from "react-intl";
import {styled} from "@mui/material/styles";

import EditableTextField from "components/input/new/EditableTextField";
import CheckboxListSelect from "components/select/CheckboxListSelect";
import RadioListSelect from "components/select/RadioListSelect";

import {FIELD_TYPES, isValidText, toApiValue} from "../attributeField";
import {targetLabelId} from "../enrichmentConstants";

TargetEditor.propTypes = {
    target: PropTypes.string.isRequired,
    field: PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string,
        options: PropTypes.array.isRequired,
        max: PropTypes.number,
        openList: PropTypes.bool,
    }).isRequired,
    value: PropTypes.any,
    suggestedValue: PropTypes.any,
    highlighted: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
};

const AI_GRADIENT = 'linear-gradient(60deg, #DC736D 0%, #DB5D58 14%, #E85576 30%, #E4489C 45%, #BE45AB 58%, #7B44BC 70%, #5671EF 86%, #739BF5 100%)';

const AiMark = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    height: '16px',
    padding: '0 4px',
    boxSizing: 'border-box',
    border: '1px solid transparent',
    borderRadius: '4px',
    background: `linear-gradient(#fff, #fff) padding-box, ${AI_GRADIENT} border-box`,
    color: '#222',
    fontFamily: '"IBM Plex Sans", Roboto, sans-serif',
    fontSize: '8px',
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '-0.16px',
});

const TEXT_INPUTS = {
    [FIELD_TYPES.STRING]: {},
    [FIELD_TYPES.FLOAT]: {inputMode: 'decimal'},
    [FIELD_TYPES.DATE]: {type: 'date'},
    [FIELD_TYPES.URL]: {inputMode: 'url'},
};

const asList = (value) => (Array.isArray(value) ? value : (value ? [value] : []));

function markSuggested(options, suggested) {
    return options.map((option) => (suggested.includes(option.value)
        ? {...option, adornment: <AiMark><FormattedMessage id="enrichment.suggestion.ai"/></AiMark>}
        : option));
}

function TargetEditor({target, field, value, suggestedValue, highlighted = false, disabled = false, onChange}) {
    const intl = useIntl();
    const placeholder = field.label ?? intl.formatMessage({id: targetLabelId(target)});
    const change = (selected) => onChange?.(toApiValue(field.type, selected));
    const common = {placeholder, highlighted, disabled, noOptionsText: <FormattedMessage id="enrichment.values.noResult"/>};

    if (TEXT_INPUTS[field.type]) {
        return (
            <div style={{flex: '1 0 0', minWidth: 0}}>
                <EditableTextField
                    {...TEXT_INPUTS[field.type]}
                    value={value ?? null}
                    placeholder={placeholder}
                    highlighted={highlighted}
                    disabled={disabled}
                    validate={(text) => isValidText(field.type, text)}
                    markedValue={suggestedValue ?? null}
                    mark={<AiMark><FormattedMessage id="enrichment.suggestion.ai"/></AiMark>}
                    onChange={change}
                />
            </div>
        );
    }

    if (FIELD_TYPES.MULTIPLE_CHOICE === field.type) {
        return (
            <div style={{flex: '1 0 0', minWidth: 0}}>
                <CheckboxListSelect
                    {...common}
                    options={markSuggested(field.options, asList(suggestedValue))}
                    value={asList(value)}
                    max={field.max}
                    searchable
                    onChange={change}
                />
            </div>
        );
    }

    const options = FIELD_TYPES.BOOL === field.type
        ? [{value: 'true', label: intl.formatMessage({id: 'enrichment.values.yes'})}, {value: 'false', label: intl.formatMessage({id: 'enrichment.values.no'})}]
        : field.options;
    const toOption = (item) => (null === item || undefined === item ? null : String(item));
    const extraValues = field.openList
        ? [...new Set([suggestedValue, value])].filter((extra) => extra && !options.some((option) => option.value === extra))
        : [];

    return (
        <div style={{flex: '1 0 0', minWidth: 0}}>
            <RadioListSelect
                {...common}
                options={markSuggested([...extraValues.map((extra) => ({value: extra, label: extra})), ...options], asList(toOption(suggestedValue)))}
                value={toOption(value)}
                searchable={FIELD_TYPES.BOOL !== field.type}
                onChange={change}
            />
        </div>
    );
}

export default TargetEditor;
